import React, { useEffect, useRef, useState } from "react";

export default function WhyChooseUs() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const reasons = [
    {
      title: "Unit Pengiriman Sesuai Pesanan",
      subtitle: "Customized Delivery Units",
      description:
        "Kami selalu memastikan bahwa setiap unit pengiriman dapat disesuaikan dengan kebutuhan dan spesifikasi customer, sehingga pengiriman dilakukan dengan efisien dan tepat sasaran.",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
    {
      title: "Pengiriman Tepat Waktu",
      subtitle: "On-Time Delivery",
      description:
        "Kami memiliki komitmen untuk mengirimkan barang tepat waktu sesuai dengan kesepakatan, tujuannya agar customer dapat memastikan dan mengandalkan kami dalam kebutuhan pengiriman barang yang mendesak atau rutin.",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      title: "Keamanan dan Ketenangan",
      subtitle: "Security and Peace of Mind",
      description:
        "Kami memberikan jaminan keamanan pada setiap pengiriman, melalui sistem tracking yang canggih dan prosedur yang ketat kami dapat memberikan ketenangan pikiran kepada customer.",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-32 gradient-mesh relative overflow-hidden noise"
    >
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-white/5 rounded-full blur-3xl animate-float"></div>
      <div
        className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-white/5 rounded-full blur-3xl animate-float"
        style={{ animationDelay: "3s" }}
      ></div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center mb-20">
          <h2
            className={`text-5xl lg:text-6xl font-bold text-white mb-6 transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-20"
            }`}
          >
            Mengapa Memilih Kami?
          </h2>
          <p
            className={`text-xl text-white/90 max-w-3xl mx-auto leading-relaxed transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-20"
            }`}
            style={{ transitionDelay: "0.1s" }}
          >
            Komitmen kami adalah kepuasan dan kepercayaan customer
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-10">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className={`glass rounded-3xl p-10 card-premium border border-white/20 transition-all duration-1000 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-20"
              }`}
              style={{ transitionDelay: `${0.2 + index * 0.15}s` }}
            >
              <div className="text-white mb-8 flex justify-center">
                <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center glow group-hover:scale-110 transition-transform duration-500">
                  {reason.icon}
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2 text-center">
                {reason.title}
              </h3>
              <p className="text-sm text-white/70 mb-6 text-center italic font-light">
                {reason.subtitle}
              </p>
              <p className="text-white/90 text-center leading-relaxed">
                {reason.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
